import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { CustomerDeliveryAddress, FormField, errorMap, ErrorMode, DeliveryApiException, DeliveryErrorCode, DeliveryApiError, CustomerProfile, OrderErrorCode } from '@/models';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { makeFormField, isValidPhoneNumber, isValidCanadaPostalCode, getCountryCodeByTopLevelDomain, removeLargeNumbers } from '@/helpers';
import { formatCanadaPostalCode, formatPhoneNumber, stripLeadingOnesAndZeros, stripNonNumerics, isValidPostalCode, isValidCustomerName } from '@/helpers';
import { external as externalConstants } from '@/constants';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { CountryCode } from '@/store/ui';
import { Routes } from '@/router';
import { AccountManagementEvent, SaveDeliveryAddressEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';

const MAX_NOTES_CHARACTERS = 200;

export interface DeliveryAddressForm {
  name: FormField;
  customerName: FormField;
  companyName: FormField;
  phoneNumber: FormField;
  streetAddress1: FormField;
  streetAddress2: FormField;
  city: FormField;
  state: FormField;
  province: FormField;
  canadaPostalCode: FormField;
  postalCode: FormField;
  notes: FormField;

  addressId?: string;
  selectedDate?: string;
  createdDate?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
}

interface FormattedProvince {
  formatted: string;
  value: string;
}

@Component({ name: 'CmgDeliveryAddressModal' })
export default class CmgDeliveryAddressModal extends Vue {
  saveAddress = false;
  isLoading = false;
  contentScrolled = false;
  form: DeliveryAddressForm = {
    name: makeFormField('Home'),
    customerName: makeFormField(''),
    companyName: makeFormField(''),
    phoneNumber: makeFormField(''),
    streetAddress1: makeFormField(''),
    streetAddress2: makeFormField(''),
    city: makeFormField(''),
    state: makeFormField(''),
    province: makeFormField(''),
    canadaPostalCode: makeFormField(''),
    postalCode: makeFormField(''),
    notes: makeFormField(''),
  };
  hasErrors = false;
  hasGetEstimateError = false;
  getEstimateErrorMessage = '';
  shouldShowProvincesPlaceholder = true;

  messageText =
    'The phone number you entered above is the number the driver will call if they need to get in touch with you. By continuing, you agree to share your contact information with the DoorDash driver that will deliver your food.';
  nicknameType: 'Home' | 'Work' | 'other' = 'Home';
  notesCharactersLeft = MAX_NOTES_CHARACTERS;

  get siteCountryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  get profileCountryCode(): string {
    return this.customer ? this.customer.country || CountryCode.US : '';
  }

  get provinces(): FormattedProvince[] {
    return externalConstants.canadianProvinces;
  }

  get formatCanadaPostalCode(): (code: string) => string {
    return formatCanadaPostalCode;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.deliveryAddress.visible;
  }

  get isSavedAddress(): boolean {
    const modalArgs = this.$appStore.state.ui.modals.deliveryAddress.args;
    return modalArgs!.isSavedAddress || false;
  }

  get closeButtonImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.xDarkBrown.default;
  }

  get checkMarkUrl(): string {
    return this.$appStore.state.cms.images.icons.checkMarkSelected.default;
  }

  get contentContainer(): HTMLElement {
    return (this.$refs.contentContainer as any) as HTMLElement;
  }

  get modalArgsAddress(): CustomerDeliveryAddress | null {
    const args = this.$appStore.state.ui.modals.deliveryAddress.args;
    return args && args.deliveryAddress ? args.deliveryAddress : null;
  }

  get shouldShowProvinceField(): boolean {
    switch (this.siteCountryCode) {
      case CountryCode.CA:
        return true;
      case CountryCode.US:
      default:
        return false;
    }
  }

  get streetAddress2Placeholder(): string {
    switch (this.siteCountryCode) {
      case CountryCode.CA:
        return 'Apt / Suite / Unit (optional)';
      case CountryCode.US:
      default:
        return 'Address 2 (optional)';
    }
  }

  get savedDeliveryAddresses() {
    return this.$appStore.state.customer.deliveryAddresses || [];
  }

  @Watch('modalArgsAddress', { immediate: true, deep: true })
  modalArgsAddressChanged(address: CustomerDeliveryAddress) {
    this.hasErrors = false;
    this.saveAddress = false;
    if (address) {
      this.form = this.customerDeliveryAddressToForm(address!);
    }
  }

  updated() {
    if (this.$refs.province) {
      const address = this.modalArgsAddress;
      if (this.shouldShowProvinceField && address) {
        const option = this.provinces.find(p => p.value == address.state);
        const index = this.provinces.findIndex(p => p.value == address.state);

        if (option) {
          (this.$refs.province as any).onSelectOption(option, index);
        }
      }
    }

    if (this.$refs.contentContainer && !this.$services.utils.iOSDevice()) {
      disableBodyScroll(this.contentContainer, {
        allowTouchMove: el => true,
      });
      return;
    }

    if (!this.$services.utils.iOSDevice()) {
      enableBodyScroll(this.contentContainer);
    }
  }

  onClose() {
    this.$services.order.setCachedDeliveryAddress(null);
    this.$services.ui.setModalVisibility('deliveryAddress', false);
    this.$services.ui.focusBack();
  }

  onContentScroll({ target: { scrollTop } }: { target: { scrollTop: number } }) {
    if (scrollTop === 0) {
      this.contentScrolled = false;
    } else {
      this.contentScrolled = true;
    }
  }

  setNicknameType(newType: 'Home' | 'Work' | 'other', otherValue?: string) {
    this.nicknameType = newType;
    if (newType === 'Home') {
      this.$nextTick(() => (this.$refs.home as HTMLInputElement).focus());
    }
    if (newType === 'Work') {
      this.$nextTick(() => (this.$refs.work as HTMLInputElement).focus());
    }
    if (newType === 'other') {
      this.$nextTick(() => (this.$refs.nicknameInput as HTMLInputElement).focus());
      this.form.name.value = otherValue!;
      return;
    }

    this.form.name.value = newType;
  }

  onName(event: Event) {
    this.form.name.value = (event.target as HTMLInputElement).value;
  }

  onNotes(event: Event) {
    this.form.notes.value = (event.target as HTMLInputElement).value;
    this.notesCharactersLeft = MAX_NOTES_CHARACTERS - this.form.notes.value.length;
    let deliveryInstructions = removeLargeNumbers(this.form.notes.value);
    this.form.notes.value = deliveryInstructions;
    this.$appStore.state.checkout.delivery.instructions.value = this.form.notes.value;
  }

  onSetSaveDeliveryAddress() {
    if (this.savedDeliveryAddresses.length < 5) {
      this.saveAddress = !this.saveAddress;
    }
  }

  async selectAddress() {
    this.isLoading = true;
    if (!this.isFormValid()) {
      this.isLoading = false;
      this.hasErrors = true;
      return;
    }

    try {
      let deliveryAddress = this.formToCustomerDeliveryAddress(this.form);
      const validationResult = await this.$services.order.validateDeliveryAddress(
        this.$services.findAChipotle.customerDeliveryAddressToValidationRequest(deliveryAddress)
      );

      if (validationResult && !(validationResult instanceof DeliveryApiException) && validationResult.statusText === 'OK') {
        // if address is already on the server update or create
        if ((this.saveAddress && this.$services.customer.isLoggedIn()) || deliveryAddress.addressId) {
          deliveryAddress = await this.$services.customer.createCustomerDeliveryAddress(deliveryAddress);
        }

        this.$services.order.setCachedDeliveryAddress(deliveryAddress);

        // If we were able to get a restaurantId we can use that to set the current restaurant
        const restaurantId = validationResult.data.restaurantId;
        if (restaurantId) {
          const restaurant = await this.$services.restaurant.getRestaurantByNumber(restaurantId);
          this.$services.order.setCachedRestaurant(restaurant);
          await this.$services.restaurant.selectRestaurant(restaurant, false);
        } else {
          await this.$services.restaurant.selectRestaurant(null, false);
        }

        // set local selected address before selecting restaurant to ensure we have all the info we need to recalculate things like estimates
        await this.$services.order.setDeliveryAddress(deliveryAddress);

        // If restaurant changes, experience request will fire on meal-builder page,
        // so no need to fire the experience request when we are on meal-builder page.
        if (this.$route.name != Routes.MealBuilder ) {
          this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);
        }

        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.Behavior,
          action: AnalyticsEventAction.Click,
          label: AnalyticsEventLabel.RestaurantSearchDeliveryCommit,
        });

        this.$services.analytics.send(new SaveDeliveryAddressEvent(this.form));

        this.$services.ui.setModalVisibility('deliveryAddress', false);

        // After selecting a delivery address, run the findAChipotle modal onClose()
        const args = this.$appStore.state.ui.modals.findAChipotle.args;
        if (args && args.onClose) {
          args.onClose();
        }
        this.$services.ui.setModalVisibility('findAChipotle', false);

        if(getCountryCodeByTopLevelDomain() === deliveryAddress.countryCode) {
          this.$services.ui.setModalVisibility('toast', !args?.suppressPickupDeliveryToast, {
            title: 'DELIVERING TO',
            subTitle: deliveryAddress.name ? deliveryAddress.name.toUpperCase() : undefined,
            body: deliveryAddress.streetAddress1!,
            legalCopy: 'Menu pricing for delivery is higher and fees apply.',
            icon: this.$appStore.state.cms.images.icons.carWhite.default,
            after: async () => {
              const epops = await this.$services.epop.getEpopsForRestaurant(restaurantId);
              if (epops && epops.length > 0) {
                this.$services.ui.setModalVisibility('epop', true, {
                  epops,
                });
                this.$services.ui.firstFocus();
              }
            },
          });
        }
        this.$services.ui.focusBack();
        // Plop phone number and name into store so we can carry over address-specific data to checkout
        this.$services.checkout.setDeliveryPhoneNumber(deliveryAddress.phoneNumber);
        this.$services.checkout.setDeliveryName(deliveryAddress.customerName);
      } else {
        this.processValidateDeliveryAddressErrors((validationResult as DeliveryApiException).errors);
        this.hasErrors = true;
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.Delivery,
          action: AnalyticsEventAction.ErrorMessage,
          label: AnalyticsEventLabel.ModalAddressCantDeliver,
        });
      }
    } catch (e) {
      this.$services.logger.error(e);

      const itemNotAvailableErrorCodes = [
        OrderErrorCode.EntreeNotAvailable,
        OrderErrorCode.SideNotAvailable,
        OrderErrorCode.DrinkNotAvailable,
        OrderErrorCode.NonFoodItemNotAvailable,
      ];

      // Don't show globalErrorToast for unavailable items because bag reconciliation modal handles it already
      if (e?.response?.data?.every((err: { errorCode: OrderErrorCode }) => itemNotAvailableErrorCodes.includes(err.errorCode))) {
        return;
      }

      this.$services.ui.setModalVisibility('globalErrorToast', true);
    } finally {
      this.isLoading = false;
    }

    const path = this.$route.fullPath;
    if (path === '/' || path === '/order') {
      this.$router.push({ name: Routes.Order, hash: '#menu' });
    } else if (path === '/order#menu' || path === '/order/#menu') {
      this.$services.ui.setShouldScrollHomeToHash(true);
    }
  }

  async saveUpdatedAddress() {
    this.isLoading = true;
    if (!this.isFormValid()) {
      this.isLoading = false;
      this.hasErrors = true;
      return;
    }
    try {
      const deliveryAddress = this.formToCustomerDeliveryAddress(this.form);
      const validationResult = await this.$services.order.validateDeliveryAddress(
        this.$services.findAChipotle.customerDeliveryAddressToValidationRequest(deliveryAddress)
      );

      if (validationResult && !(validationResult instanceof DeliveryApiException) && validationResult.statusText === 'OK') {
        await this.$services.customer.updateCustomerDeliveryAddress(deliveryAddress);
        this.$services.ui.setModalVisibility('deliveryAddress', false);
        this.$services.ui.focusBack();
        this.hasGetEstimateError = false;
        this.$services.analytics.send(new AccountManagementEvent(InteractionTrackingType.EditAddress));
      } else {
        this.processValidateDeliveryAddressErrors((validationResult as DeliveryApiException).errors);
        this.hasErrors = true;
      }
    } catch (e) {
      this.getEstimateErrorMessage = errorMap(e.response.data.errorCode);
      this.hasGetEstimateError = true;
      this.$services.logger.error(e);
    }
    this.isLoading = false;
  }

  async deleteAddress() {
    this.isLoading = true;
    try {
      const deliveryAddress = this.formToCustomerDeliveryAddress(this.form);
      await this.$services.customer.deleteCustomerDeliveryAddress(deliveryAddress.addressId!);
    } catch (e) {
      this.$services.logger.error(e);
      this.$services.ui.setModalVisibility('globalErrorToast', true);
    } finally {
      this.$services.ui.setModalVisibility('deliveryAddress', false);
      this.isLoading = false;
      this.$services.ui.nextFocus('delivery-address-h3');
    }
  }

  customerDeliveryAddressToForm(address: CustomerDeliveryAddress): DeliveryAddressForm {
    const profile = this.$appStore.state.customer.profile;
    if (profile && !profile.isGuest) {
      if (!address.customerName) {
        address.customerName = `${profile.firstName} ${profile.lastName}`.trim();
      }
      if (!address.phoneNumber) {
        address.phoneNumber = profile.phoneNumber;
      }
    }

    this.setNicknameType(!!address.name ? 'other' : 'Home');

    const formFields = {
      name: makeFormField(address.name || 'Home'),
      customerName: makeFormField(address.customerName || ''),
      companyName: makeFormField(address.companyName || ''),
      phoneNumber: address.phoneNumber
        ? { value: address.phoneNumber, dirty: false, formatted: this.formatPhoneNumber(address.phoneNumber), error: null, errorMode: null }
        : makeFormField(''),
      streetAddress1: makeFormField(address.streetAddress1 || ''),
      streetAddress2: makeFormField(address.streetAddress2 || ''),
      city: makeFormField(address.city || ''),
      state: makeFormField(address.state || ''),
      province: makeFormField(address.state || ''),
      canadaPostalCode: makeFormField(formatCanadaPostalCode(address.postalCode || '')),
      postalCode: makeFormField(address.postalCode || ''),
      notes: makeFormField(address.notes || ''),
    };

    const deliveryAddressForm = Object.assign({}, address, formFields) as DeliveryAddressForm;
    return deliveryAddressForm;
  }

  formToCustomerDeliveryAddress(form: DeliveryAddressForm): CustomerDeliveryAddress {
    let postalCode;
    let countryCode;

    switch (this.siteCountryCode) {
      case CountryCode.CA:
        postalCode = form.canadaPostalCode.value;
        countryCode = CountryCode.CA;
        break;
      case CountryCode.US:
      default:
        postalCode = form.postalCode.value;
        countryCode = CountryCode.US;
    }

    return {
      name: form.name.value,
      customerName: form.customerName.value,
      companyName: form.companyName.value,
      phoneNumber: form.phoneNumber.value,
      streetAddress1: form.streetAddress1.value,
      streetAddress2: form.streetAddress2.value,
      city: form.city.value,
      state: form.state.value,
      province: form.province.value,
      postalCode,
      notes: form.notes.value,

      addressId: form.addressId,
      selectedDate: form.selectedDate,
      createdDate: form.createdDate,
      countryCode,
      latitude: form.latitude,
      longitude: form.longitude,
    };
  }

  onSelectProvince(value: string): void {
    this.shouldShowProvincesPlaceholder = false;
    this.form.province.value = value;
  }

  processValidateDeliveryAddressErrors(errors: DeliveryApiError[]) {
    errors.forEach((error: DeliveryApiError) => {
      switch (error.errorCode) {
        case DeliveryErrorCode.DropoffAddressLine1IsRequired:
          this.form.streetAddress1.error = 'Address Invalid';
          this.form.streetAddress1.errorMode = ErrorMode.BelowField;
          break;
        case DeliveryErrorCode.DropoffAddressCityIsRequired:
          this.form.city.error = 'City Invalid';
          this.form.city.errorMode = ErrorMode.BelowField;
          break;
        case DeliveryErrorCode.DropoffAddressStateIsRequired:
          this.form.state.error = 'State';
          this.form.state.errorMode = ErrorMode.BelowField;
          break;
        case DeliveryErrorCode.DropoffAddressZipcodeIsRequired:
          switch (this.siteCountryCode) {
            case CountryCode.CA:
              this.form.canadaPostalCode.error = 'Postal Code';
              this.form.canadaPostalCode.errorMode = ErrorMode.BelowField;
              break;
            case CountryCode.US:
            default:
              this.form.postalCode.error = 'Zip';
              this.form.postalCode.errorMode = ErrorMode.BelowField;
              break;
          }
          break;
      }
    });
  }

  isFormValid(): boolean {
    const customerNameValid = this.validateCustomerName();
    const phoneNumberValid = this.validatePhoneNumber();
    const streetAddress1Valid = this.validateStreetAddress1();
    const cityValid = this.validateCity();
    const stateValid = this.validateState();
    const postalCodeValid = this.validatePostalCode();
    const provinceValid = this.validateProvince();

    const isFormValid = customerNameValid && phoneNumberValid && streetAddress1Valid && cityValid && stateValid && postalCodeValid && provinceValid;

    this.form.customerName.dirty = true;
    this.form.phoneNumber.dirty = true;
    this.form.streetAddress1.dirty = true;
    this.form.city.dirty = true;
    this.form.state.dirty = true;
    this.form.postalCode.dirty = true;
    this.form.canadaPostalCode.dirty = true;

    if (this.form.name.value === '') {
      this.form.name.value = 'OTHER';
    }

    return isFormValid;
  }

  validateCustomerName(): boolean {
    const name = this.form.customerName;
    name.error = ' ';
    name.errorMode = ErrorMode.PlaceHolder;
    if (name.value.length == 0) {
      name.error = 'Customer Name Required';
      return false;
    }
    else if (!isValidCustomerName(name.value)){
      name.error = 'No special characters allowed';
      return false;
    }
    else {
    name.error = '';
    this.checkAllFieldsForErrors();
    return true;
    }
  }

  removeLargeNumbersCompanyName(){
    this.form.companyName.value = removeLargeNumbers(this.form.companyName.value);
  }

  validatePhoneNumber(): boolean {
    const phoneNumber = this.form.phoneNumber;
    if (phoneNumber.value.length == 0) {
      phoneNumber.error = 'Phone Number Required';
      phoneNumber.errorMode = ErrorMode.PlaceHolder;
      return false;
    } else if (!isValidPhoneNumber(phoneNumber.value)) {
      phoneNumber.error = 'Phone number is incomplete';
      phoneNumber.errorMode = ErrorMode.BelowField;
      return false;
    }
    phoneNumber.error = '';
    phoneNumber.errorMode = ErrorMode.PlaceHolder;
    this.checkAllFieldsForErrors();
    return true;
  }

  validateStreetAddress1(): boolean {
    const address = this.form.streetAddress1;
    if (address.value.length == 0) {
      address.error = 'Address Required';
      address.errorMode = ErrorMode.PlaceHolder;
      return false;
    }
    address.error = '';
    this.checkAllFieldsForErrors();
    return true;
  }

  removeLargeNumbersAddress() {
    this.form.streetAddress1.value = removeLargeNumbers(this.form.streetAddress1.value);
    this.form.streetAddress2.value = removeLargeNumbers(this.form.streetAddress2.value);
    }
  
  validateCity(): boolean {
    const city = this.form.city;
    if (city.value.length == 0) {
      city.error = 'City Required';
      city.errorMode = ErrorMode.PlaceHolder;
      return false;
    }
    city.error = '';
    this.checkAllFieldsForErrors();
    return true;
  }

  removeLargeNumbersCity(){
    this.form.city.value = removeLargeNumbers(this.form.city.value);
  }

  validateState(): boolean {
    switch (this.siteCountryCode) {
      case CountryCode.CA:
        return true;
      case CountryCode.US:
      default:
        const state = this.form.state;

        if (state.value.length == 0 || externalConstants.stateCodes.indexOf(state.value.toUpperCase()) === -1) {
          state.error = 'State';
          state.errorMode = ErrorMode.PlaceHolder;
          return false;
        }

        state.error = '';
        state.errorMode = ErrorMode.PlaceHolder;
        this.checkAllFieldsForErrors();
        return true;
    }
  }

  validatePostalCode(): boolean {
    switch (this.siteCountryCode) {
      case CountryCode.CA:
        return this.validateCanadaPostalCode();
      case CountryCode.US:
      default:
        const postalCode = this.form.postalCode;
        if (postalCode.value.length === 0 || !isValidPostalCode(postalCode.value)) {
          postalCode.error = 'Zip';
          postalCode.errorMode = ErrorMode.PlaceHolder;
          return false;
        }

        postalCode.error = '';
        postalCode.errorMode = ErrorMode.PlaceHolder;
        this.checkAllFieldsForErrors();
        return true;
    }
  }

  validateCanadaPostalCode(): boolean {
    const postalCode = this.form.canadaPostalCode;

    // Remove white space when it is auto filled
    if (postalCode.value.length == 7) {
      postalCode.formatted = postalCode.value;
      postalCode.value = postalCode.value.replace(/ /g, '');
    }

    if (!isValidCanadaPostalCode(postalCode.value)) {
      postalCode.error = 'Postal Code';
      postalCode.errorMode = ErrorMode.PlaceHolder;
      return false;
    }

    postalCode.error = '';
    postalCode.errorMode = ErrorMode.PlaceHolder;
    this.checkAllFieldsForErrors();
    return true;
  }

  validateProvince(): boolean {
    switch (this.siteCountryCode) {
      case CountryCode.CA:
        const province = this.form.province;

        if (!province.value) {
          province.error = 'Province';
          province.errorMode = ErrorMode.PlaceHolder;
          return false;
        }

        province.error = '';
        this.checkAllFieldsForErrors();
        return true;
      case CountryCode.US:
      default:
        return true;
    }
  }

  checkAllFieldsForErrors() {
    this.hasErrors =
      !!this.form.customerName.error ||
      !!this.form.phoneNumber.error ||
      !!this.form.streetAddress1.error ||
      !!this.form.city.error ||
      !!this.form.state.error ||
      !!this.form.postalCode.error ||
      !!this.form.canadaPostalCode.error ||
      !!this.form.province.error;
  }

  formatPhoneNumber(value: string): string {
    return formatPhoneNumber(value);
  }

  sanitizePhoneNumber(value: string): string {
    return stripLeadingOnesAndZeros(stripNonNumerics(value));
  }
}
